import React, { useState } from 'react';
/**
 * LiveRulebookCard — Renders a single ModuleRulebook from the live
 * RULEBOOKS_REGISTRY (governance/rulebooks). Phase 3 Strak-Trek.
 *
 * Replaces the static markdown-metadata RulebookCard for module rulebooks.
 * Every section here is sourced from executable code — what you see is what
 * the pipeline actually enforces.
 *
 * Also surfaces a "Toekomstig (roadmap)" panel sourced from
 * src/governance/rulebooks/roadmaps.ts — aspirational future question types
 * and visuals that aren't enforced yet but signal the design trajectory.
 */
import { ChevronDownIcon, ChevronRightIcon, BookOpenIcon, CheckCircleIcon, TypeIcon, AlertTriangleIcon, PaletteIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import type { ModuleRulebook } from '../../../../governance';
import { getModuleRoadmap } from '../../../../governance';
import { RULEBOOK_COLOR_MAP } from './governanceRulesData';
type Props = {
  rulebook: ModuleRulebook;
  color: string;
};
export function LiveRulebookCard({
  rulebook,
  color
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const c = RULEBOOK_COLOR_MAP[color] ?? RULEBOOK_COLOR_MAP.blue;
  const notationFlags: string[] = [];
  if (rulebook.notation.requireLatexFractions) notationFlags.push('LaTeX-breuken');
  if (rulebook.notation.requireCommaDecimals) notationFlags.push('Komma-decimalen');
  if (rulebook.notation.requireSuperscriptUnits) notationFlags.push('Superscript-eenheden (m², m³)');
  if (rulebook.notation.requireEuroComma) notationFlags.push('Euro-komma');
  const roadmap = getModuleRoadmap(rulebook.moduleId);
  return <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-id="element-4494">
      <button className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-4495">
        <BookOpenIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${c.badge.split(' ')[1]}`} data-id="element-4496" />
        <div className="min-w-0 flex-1" data-id="element-4497">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-4498">
            <span className="font-semibold text-gray-800" data-id="element-4499">
              {rulebook.displayName}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.badge}`} data-id="element-4500">
              {rulebook.shortCode}
            </span>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-4501">
              live
            </span>
            {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100 flex items-center gap-1" data-id="element-4502">
                  <SparklesIcon className="h-3 w-3" data-id="element-4503" />
                  {roadmap.futureQuestionTypes.length + roadmap.futureVisuals.length}{' '}
                  toekomstig
                </span>}
          </div>
          <p className="text-sm text-gray-600 mt-1" data-id="element-4504">
            {rulebook.didactics.coreSummary}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-4505">
            {rulebook.didactics.principles.slice(0, 4).map(p => <span key={p} className="rounded px-1.5 py-0.5 text-[10px] font-mono bg-gray-50 text-gray-500 border border-gray-100" data-id="element-4506">
                {p}
              </span>)}
          </div>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-4507">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-4508" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-4509" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${c.border} ${c.bg} px-4 py-3 space-y-4`} data-id="element-4510">
          {/* Key rules */}
          <Section icon={<CheckCircleIcon className="h-4 w-4 text-gray-400" data-id="element-4512" />} title="Kernregels" data-id="element-4511">
            <ul className="space-y-1.5" data-id="element-4513">
              {rulebook.keyRules.map((rule, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-4514">
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" data-id="element-4515" />
                  {rule}
                </li>)}
            </ul>
          </Section>

          {/* Didactics */}
          <Section icon={<LayersIcon className="h-4 w-4 text-gray-400" data-id="element-4517" />} title="Didactische context" data-id="element-4516">
            <div className="space-y-1.5 text-sm" data-id="element-4518">
              <DetailRow label="Schoolboek-methodes" value={rulebook.didactics.schoolbookReferences.join(', ')} data-id="element-4519" />
              <DetailRow label="Misconceptie-domein" value={<code className="font-mono text-xs" data-id="element-4521">
                    {rulebook.didactics.misconceptionDomain}
                  </code>} data-id="element-4520" />
            </div>
          </Section>

          {/* Notation */}
          {(notationFlags.length > 0 || (rulebook.notation.forbiddenPatterns?.length ?? 0) > 0) && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4523" />} title="Notatie" data-id="element-4522">
              {notationFlags.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-4524">
                  {notationFlags.map(flag => <span key={flag} className="rounded-full px-2 py-0.5 text-xs bg-white border border-gray-200 text-gray-700" data-id="element-4525">
                      {flag}
                    </span>)}
                </div>}
              {rulebook.notation.forbiddenPatterns?.map((p, i) => <p key={i} className="text-xs text-red-600" data-id="element-4526">
                  <code className="font-mono bg-red-50 px-1.5 py-0.5 rounded mr-2" data-id="element-4527">
                    {p.pattern}
                  </code>
                  {p.label}
                </p>)}
            </Section>}

          {/* Visuals */}
          {(rulebook.visuals.preferredAidTypes?.length ?? 0) > 0 && <Section icon={<PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-4529" />} title="Voorkeurs-visuals" data-id="element-4528">
              <div className="flex flex-wrap gap-1.5" data-id="element-4530">
                {rulebook.visuals.preferredAidTypes?.map(v => <span key={v} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4531">
                    {v}
                  </span>)}
              </div>
              {rulebook.visuals.familyVisualHints && rulebook.visuals.familyVisualHints.length > 0 && <div className="mt-2 space-y-1" data-id="element-4532">
                    {rulebook.visuals.familyVisualHints.map((h, i) => <p key={i} className="text-xs text-gray-600" data-id="element-4533">
                        <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded mr-1.5" data-id="element-4534">
                          {h.familyIdPattern}
                        </code>
                        {h.note}
                      </p>)}
                  </div>}
            </Section>}

          {/* Question types */}
          {rulebook.questionTypes.preferredTypes.length > 0 && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4536" />} title="Voorkeurs-vraagtypes" data-id="element-4535">
              <div className="flex flex-wrap gap-1.5" data-id="element-4537">
                {rulebook.questionTypes.preferredTypes.map(t => <span key={t} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4538">
                    {t}
                  </span>)}
              </div>
              {rulebook.questionTypes.customTypeRoles && rulebook.questionTypes.customTypeRoles.length > 0 && <div className="mt-2 space-y-1" data-id="element-4539">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4540">
                      Custom-rollen
                    </p>
                    {rulebook.questionTypes.customTypeRoles.map(r => <div key={r.type} className="text-xs text-gray-600 flex items-center gap-2" data-id="element-4541">
                        <code className="font-mono" data-id="element-4542">{r.type}</code>
                        <span className="text-gray-400" data-id="element-4543">·</span>
                        <span data-id="element-4544">{r.didacticRole}</span>
                        <span className="text-gray-400" data-id="element-4545">·</span>
                        <span data-id="element-4546">DOK {r.preferredDok}</span>
                      </div>)}
                  </div>}
            </Section>}

          {/* Distractor traps */}
          {rulebook.distractorTraps.length > 0 && <Section icon={<AlertTriangleIcon className="h-4 w-4 text-gray-400" data-id="element-4548" />} title="Trap-distractors (val-afleiders)" data-id="element-4547">
              <ul className="space-y-1.5" data-id="element-4549">
                {rulebook.distractorTraps.map(trap => <li key={trap.id} className="text-sm" data-id="element-4550">
                    <p className="font-medium text-gray-800" data-id="element-4551">{trap.label}</p>
                    <p className="text-xs text-gray-600" data-id="element-4552">{trap.description}</p>
                  </li>)}
              </ul>
            </Section>}

          {/* Toekomstig (roadmap) — aspirational, not enforced */}
          {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-3" data-id="element-4553">
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2 flex items-center gap-1.5" data-id="element-4554">
                  <SparklesIcon className="h-4 w-4" data-id="element-4555" />
                  Toekomstig — roadmap (niet afgedwongen)
                </p>
                <p className="text-[11px] text-violet-700/80 mb-3 leading-relaxed" data-id="element-4556">
                  Aspirationele vraagtypes en visuals die we voor deze module
                  willen bouwen. Geen pipeline-check, wel ontwerprichting.
                </p>

                {roadmap.futureQuestionTypes.length > 0 && <div className="mb-3" data-id="element-4557">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4558">
                      <TypeIcon className="h-3 w-3" data-id="element-4559" />
                      Vraagtypes ({roadmap.futureQuestionTypes.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4560">
                      {roadmap.futureQuestionTypes.map(t => <li key={t.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4561">
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4562">
                            <span className="font-medium text-gray-800" data-id="element-4563">
                              {t.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4564">
                              {t.id}
                            </code>
                            {t.tierWindow && <span className="text-[10px] text-gray-500" data-id="element-4565">
                                tier {t.tierWindow.min}–{t.tierWindow.max}
                              </span>}
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4566">
                            {t.rationale}
                          </p>
                          {t.targetFamilyPatterns && t.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4567">
                                {t.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4568">
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}

                {roadmap.futureVisuals.length > 0 && <div data-id="element-4569">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4570">
                      <PaletteIcon className="h-3 w-3" data-id="element-4571" />
                      Visuals ({roadmap.futureVisuals.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4572">
                      {roadmap.futureVisuals.map(v => <li key={v.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4573">
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4574">
                            <span className="font-medium text-gray-800" data-id="element-4575">
                              {v.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4576">
                              {v.id}
                            </code>
                            <span className={`text-[9px] uppercase tracking-wide px-1 rounded ${v.usage === 'stimulus' ? 'bg-blue-50 text-blue-700' : v.usage === 'support' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-4577">
                              {v.usage}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4578">
                            {v.rationale}
                          </p>
                          {v.targetFamilyPatterns && v.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4579">
                                {v.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4580">
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}
              </div>}
        </div>}
    </div>;
}
function Section({
  icon,
  title,
  children
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-4581">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5" data-id="element-4582">
        {icon}
        {title}
      </p>
      {children}
    </div>;
}
function DetailRow({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div className="flex items-start gap-2 text-sm" data-id="element-4583">
      <span className="text-gray-500 min-w-[140px]" data-id="element-4584">{label}:</span>
      <span className="text-gray-700" data-id="element-4585">{value}</span>
    </div>;
}